import { NavLink, useLocation } from 'react-router-dom'
import { useRef, useLayoutEffect, useState, forwardRef } from 'react'
import { useUser, useClerk, SignedIn, SignedOut, SignInButton, SignUpButton } from '@clerk/clerk-react'
import { SubscriptionDetailsButton } from '@clerk/clerk-react/experimental'
import { cn } from '@/lib/utils'
import { CreditCard, LogOut, LogIn, Crown, HelpCircle } from 'lucide-react'
import { mainNavItems, createNavItems, utilitiesNavItems, type NavItem } from '@/config/navigation'
import { Skeleton, ProfileSkeleton, NavItemSkeleton, AuthModal } from '@/components/ui'
import { useNavItemAccess } from '@/hooks'

export function Sidebar() {
  return (
    <aside className="w-64 h-full glass border-y-0 border-l-0 flex flex-col overflow-hidden">
      {/* Scrollable nav section */}
      <div className="flex-1 min-h-0 pt-6 pb-4 overflow-y-auto overscroll-none">
        <SidebarNav />
      </div>
      {/* Legal links + profile section at bottom */}
      <div className="shrink-0">
        <LegalLinks />
        <ProfileSection />
      </div>
    </aside>
  )
}

function SidebarNav() {
  const location = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const itemRefs = useRef<Map<string, HTMLAnchorElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 })

  // Combine all items for indicator tracking
  const allItems = [...mainNavItems, ...createNavItems, ...utilitiesNavItems]

  // Find active item based on current path
  const activeHref = allItems.find((item) =>
    item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
  )?.href

  // Use useEffect instead of useLayoutEffect - less blocking
  // Use requestAnimationFrame for smoother updates
  useLayoutEffect(() => {
    if (!activeHref || !navRef.current) {
      setIndicatorStyle((s) => ({ ...s, opacity: 0 }))
      return
    }

    // Defer layout calculation to next frame
    const rafId = requestAnimationFrame(() => {
      const activeEl = itemRefs.current.get(activeHref)
      if (activeEl && navRef.current) {
        const navRect = navRef.current.getBoundingClientRect()
        const activeRect = activeEl.getBoundingClientRect()
        setIndicatorStyle({
          top: activeRect.top - navRect.top,
          height: activeRect.height,
          opacity: 1,
        })
      }
    })

    return () => cancelAnimationFrame(rafId)
  }, [activeHref])

  return (
    <nav ref={navRef} className="px-3 relative">
      {/* Sliding glass indicator */}
      <div
        className={cn(
          'absolute left-3 right-3 rounded-lg bg-white/[0.05] border border-white/[0.1] transition-all duration-300 ease-out pointer-events-none',
          !activeHref && 'opacity-0'
        )}
        style={{
          top: indicatorStyle.top,
          height: indicatorStyle.height,
          opacity: indicatorStyle.opacity,
        }}
      />

      {/* Main nav items */}
      <div className="space-y-1.5">
        {mainNavItems.map((item) => (
          <NavButton
            key={item.href}
            item={item}
            ref={(el) => {
              if (el) itemRefs.current.set(item.href, el)
              else itemRefs.current.delete(item.href)
            }}
          />
        ))}
      </div>

      {/* Create section */}
      <div className="mt-6">
        <div className="px-4 pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
          Create
        </div>
        <div className="space-y-1.5">
          {createNavItems.map((item) => (
            <NavButton
              key={item.href}
              item={item}
              ref={(el) => {
                if (el) itemRefs.current.set(item.href, el)
                else itemRefs.current.delete(item.href)
              }}
            />
          ))}
        </div>
      </div>

      {/* Utilities section */}
      <div className="mt-6">
        <div className="px-4 pb-2 text-xs font-medium text-[#555] uppercase tracking-wider">
          Utilities
        </div>
        <div className="space-y-1.5">
          {utilitiesNavItems.map((item) => (
            <NavButton
              key={item.href}
              item={item}
              ref={(el) => {
                if (el) itemRefs.current.set(item.href, el)
                else itemRefs.current.delete(item.href)
              }}
            />
          ))}
        </div>
      </div>
    </nav>
  )
}

function LegalLinks() {
  return (
    <div className="px-5 py-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-white/[0.06]">
      <NavLink to="/terms" className="text-[10px] text-[#444] hover:text-[#888] transition-colors">Terms</NavLink>
      <NavLink to="/privacy" className="text-[10px] text-[#444] hover:text-[#888] transition-colors">Privacy</NavLink>
      <NavLink to="/acceptable-use" className="text-[10px] text-[#444] hover:text-[#888] transition-colors">Acceptable Use</NavLink>
    </div>
  )
}

function ProfileSection() {
  const { user, isLoaded } = useUser()
  const { signOut } = useClerk()

  // Show skeleton while Clerk is loading
  if (!isLoaded) {
    return <ProfileSkeleton />
  }

  return (
    <div className="p-3 border-t border-white/[0.08]">
      <SignedIn>
        {/* User info */}
        <div className="flex items-center gap-3 px-2 py-2">
          <img
            src={user?.imageUrl}
            alt={user?.fullName || 'Profile'}
            className="size-10 rounded-full"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.fullName || user?.username}
            </p>
            <p className="text-xs text-[#555] truncate">
              {user?.primaryEmailAddress?.emailAddress}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-0.5">
          {/* Subscription button with Clerk's subscription details popover */}
          <SubscriptionDetailsButton>
            <button className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[13px] text-[#888] hover:text-white hover:bg-white/[0.04] transition-colors">
              <CreditCard className="size-4" strokeWidth={1.5} />
              Subscription
            </button>
          </SubscriptionDetailsButton>
          <NavLink
            to="/support"
            className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[13px] text-[#888] hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            <HelpCircle className="size-4" strokeWidth={1.5} />
            Support
          </NavLink>
          <ProfileButton icon={LogOut} label="Sign out" onClick={() => signOut()} />
        </div>
      </SignedIn>
      <SignedOut>
        <div className="space-y-2">
          <SignUpButton mode="modal">
            <button className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 transition-colors">
              Sign Up Free
            </button>
          </SignUpButton>
          <SignInButton mode="modal">
            <button className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-white bg-white/[0.05] border border-white/[0.1] hover:bg-white/[0.08] transition-colors">
              <LogIn className="size-4" />
              Log In
            </button>
          </SignInButton>
        </div>
      </SignedOut>
    </div>
  )
}

interface ProfileButtonProps {
  icon: React.ElementType
  label: string
  onClick: () => void
}

function ProfileButton({ icon: Icon, label, onClick }: ProfileButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-[13px] text-[#888] hover:text-white hover:bg-white/[0.04] transition-colors"
    >
      <Icon className="size-4" strokeWidth={1.5} />
      {label}
    </button>
  )
}

interface NavButtonProps {
  item: NavItem
}

const NavButton = forwardRef<HTMLAnchorElement, NavButtonProps>(({ item }, ref) => {
  const Icon = item.icon
  const [showAuthModal, setShowAuthModal] = useState(false)
  const {
    isLoading,
    showUpgradeGradient,
    showFeatureGate,
    showSkeletonCrown,
    crownColor,
    label,
    shouldShowAuthOnClick,
  } = useNavItemAccess(item)

  // Show skeleton for special button while loading
  if (item.isSpecial && isLoading) {
    return <NavItemSkeleton />
  }

  // If not signed in and clicking upgrade button, show auth modal instead of navigating
  if (showUpgradeGradient && shouldShowAuthOnClick) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)] hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
        >
          <Icon className="size-[18px]" />
          {label}
        </button>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          variant="feature"
        />
      </>
    )
  }

  if (showUpgradeGradient) {
    return (
      <NavLink
        ref={ref}
        to={item.href}
        end={item.href === '/'}
        className="relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 bg-gradient-to-r from-violet-600/60 via-violet-400/60 to-violet-600/60 border border-violet-400/50 text-white shadow-[0_0_12px_rgba(139,92,246,0.2)] hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
      >
        <Icon className="size-[18px]" />
        {label}
      </NavLink>
    )
  }

  // For items requiring a plan - show auth modal if not signed in
  if (shouldShowAuthOnClick) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors duration-200 text-[#666] hover:text-white hover:bg-white/[0.04]"
        >
          <Icon className="size-[18px]" />
          <span className="flex-1 text-left">{label}</span>
          {showSkeletonCrown && <Skeleton className="size-4 rounded" />}
          {showFeatureGate && (
            <Crown className={cn("size-4", crownColor)} />
          )}
        </button>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          variant="feature"
        />
      </>
    )
  }

  return (
    <NavLink
      ref={ref}
      to={item.href}
      end={item.href === '/'}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200',
          isActive ? 'text-white' : 'text-[#666] hover:text-white hover:bg-white/[0.04]'
        )
      }
    >
      <Icon className="size-[18px]" />
      <span className="flex-1">{label}</span>
      {showSkeletonCrown && <Skeleton className="size-4 rounded" />}
      {showFeatureGate && (
        <Crown className={cn("size-4", crownColor)} />
      )}
    </NavLink>
  )
})

NavButton.displayName = 'NavButton'
